// Considered: extending ArcIntentInspectorMolecule — rejected because a stage action's shape
// (types/arc.d.ts:165-299, a 7-member discriminated union) shares no fields with an intent beyond
// "lives in the side rail". Considered: the prototype's flat ActionEditor shape (`type`/`ref`/
// `subject`/`stage`/`target`/`hook`/`tier`/`retry` via one generic RefPicker,
// arc-inspector.jsx:566-646) — rejected because it edits a fictional artifact registry with no
// backing model; this molecule is type-switched over the real union instead (D17,
// arc-editor-canvas-v1.md Open Questions). See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { Button } from 'primereact/button';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import ChoiceCardGroupAtom from '../../atoms/ChoiceCardGroup/ChoiceCardGroupAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import TypeFieldRowMolecule from '../studio/TypeFieldRowMolecule';
import InlineConfirmMolecule from '../studio/InlineConfirmMolecule';
import UnifiedFilterTreeMolecule from '../UnifiedFilterTree/UnifiedFilterTreeMolecule';
import { withStableFilterGroupIds } from '../../../utils/filterGroupIds';
import { seedLeavesIntoGroups, topLevelRowToLeaf } from '../../../utils/filterGroupSeeding';
import { simpleGroupToUnified, unifiedGroupToSimple } from '../../../utils/contextFlowSerialization';
import { nextFilterId } from '../../../utils/ids';
import type { FilterState } from '../../../types/filterState';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import type { FilterGroup } from 'types/filters';
import { focusPrimeButton } from '../../../utils/focusPrimeButton';
import type { AutomationTier } from 'types/authority';
import './ArcActionInspectorMolecule.scss';

/** The four kinds this molecule can author. The three factor kinds (`attach_status_factor`,
 * `modify_status_factor`, `deactivate_status_factor`) are configured outside the canvas: the host
 * offers them only as the current type (so the select reads truthfully), with `refOptions: null`
 * and a `typeNote` saying where they are configured. */
export type ArcActionAuthoredType = 'deploy_approach' | 'attach_aspect' | 'detach_aspect' | 'set_aspect_value';

export type ArcActionHook = 'onEnter' | 'onExit';

export interface ArcActionInspectorSelectOption {
  value: string;
  label: string;
}

export interface ArcActionInspectorMoleculeProps {
  /** Plain-language read-back of the action, pre-composed by the host (mirrors
   * `acActionSentence`). */
  sentence: string;
  /** The current type — one of the authored four, or a factor kind the host lists as the
   * current option only. */
  actionType: ArcActionAuthoredType | string;
  onActionTypeChange: (type: ArcActionAuthoredType) => void;
  actionTypeOptions: ArcActionInspectorSelectOption[];
  /** Localized note for the selected type (`AC_ACTION_TYPES[type].note`, real-model copy). */
  typeNote: string;
  /** "Which approach" / "Which aspect" — depends on `actionType`. */
  refLabel: string;
  refValue: string;
  /** `null` — the current type has no single reference to pick (the factor kinds), so the
   * picker is not rendered. */
  refOptions: ArcActionInspectorSelectOption[] | null;
  onRefChange: (value: string) => void;
  /** Shown under the picker when the catalog it offers could not be loaded (S2: what, why, how). */
  refErrorMessage?: string;
  /** Provenance chip beside the picker once a value is chosen; `null` renders nothing
   * (`side.chip#2` — `value=yes` state only). */
  refChip: { label: string; icon?: string } | null;
  stageId: string;
  stageOptions: ArcActionInspectorSelectOption[];
  onStageChange: (value: string) => void;
  targetKind: string;
  targetKindOptions: ArcActionInspectorSelectOption[];
  onTargetKindChange: (value: string) => void;
  /** Localized note for the selected target kind (`AC_TARGETS[kind].note`, real-model copy). */
  targetNote: string;
  /** `descendant` only — the filter narrowing which descendants match. */
  targetFilters?: FilterState;
  onTargetFiltersChange: (filters: FilterState) => void;
  targetFiltersAvailableProperties: PropertyOption[];
  /** `descendant` and `context_member` — how many matches the resolver returns. */
  targetSelection: 'first' | 'all';
  onTargetSelectionChange: (value: 'first' | 'all') => void;
  /** `descendant` only; integer >= 1. `undefined` clears it (unlimited depth). */
  targetMaxDepth?: number;
  onTargetMaxDepthChange: (value: number | undefined) => void;
  /** `context_member` only. */
  targetContextSystemName: string;
  onTargetContextSystemNameChange: (value: string) => void;
  targetContextOptions: ArcActionInspectorSelectOption[];
  /** `created_by_action` only — options are the earlier actions in the same hook. */
  targetActionId: string;
  onTargetActionIdChange: (value: string) => void;
  targetActionOptions: ArcActionInspectorSelectOption[];
  hook: ArcActionHook;
  onHookChange: (hook: ArcActionHook) => void;
  tier: AutomationTier;
  onTierChange: (tier: AutomationTier) => void;
  editable: boolean;
  onApply: () => void;
  /** Called only after the inline confirm is accepted. */
  onRemove: () => void;
  className?: string;
}

const HOOK_OPTIONS: Array<{ value: ArcActionHook; icon: string }> = [
  { value: 'onEnter', icon: 'lucide:log-in' },
  { value: 'onExit', icon: 'lucide:log-out' },
];

const TIER_OPTIONS: Array<{ value: AutomationTier; held: boolean }> = [
  { value: 'suggest', held: true },
  { value: 'draft', held: true },
  { value: 'confirm', held: true },
  { value: 'delegated', held: false },
  { value: 'automatic', held: false },
];

/**
 * The `descendant` target's `maxDepth`: blur-validated integer >= 1, blank clears it (unlimited
 * depth). Unlike `TypeFieldRowMolecule`'s built-in `number` control — which silently reverts an
 * invalid draft on blur — this field keeps the invalid draft on screen and names the problem, per
 * spec `inspectorRendersConfigPerKind` (0 / -1 / 1.5 show the error; 3 clears it; blank is
 * allowed). Its own local draft/error state, so it never has to reach into the parent form.
 */
const ArcActionTargetMaxDepthField: React.FC<{
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  editable: boolean;
  label: string;
  hint: string;
  errorText: string;
}> = ({ value, onChange, editable, label, hint, errorText }) => {
  const [draft, setDraft] = React.useState<string | null>(null);
  const [invalid, setInvalid] = React.useState(false);
  const committed = value === undefined ? '' : String(value);

  const commit = () => {
    if (draft === null) return;
    const trimmed = draft.trim();
    if (trimmed === '') {
      setInvalid(false);
      setDraft(null);
      if (value !== undefined) onChange(undefined);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 1) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setDraft(null);
    if (parsed !== value) onChange(parsed);
  };

  return (
    <TypeFieldRowMolecule
      label={label}
      hint={hint}
      layout="stacked"
      mode={editable ? 'edit' : 'disabled'}
      errorMessage={invalid ? errorText : undefined}
      controls={[
        {
          kind: 'custom',
          ariaLabel: label,
          node: (
            <InputAtom
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              aria-label={label}
              value={draft ?? committed}
              disabled={!editable}
              readOnly={!editable}
              onChange={editable ? (e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value) : undefined}
              onBlur={editable ? commit : undefined}
            />
          ),
        },
      ]}
    />
  );
};

/** The action editor the inspector rail's `selection.body` hosts for `selection.kind === 'action'`
 * (arc-editor-canvas.md `side.panel#5`…`side.remove#2`, `arc-inspector.jsx:566-646`). */
const ArcActionInspectorMolecule: React.FC<ArcActionInspectorMoleculeProps> = ({
  sentence,
  actionType,
  onActionTypeChange,
  actionTypeOptions,
  typeNote,
  refLabel,
  refValue,
  refOptions,
  onRefChange,
  refErrorMessage,
  refChip,
  stageId,
  stageOptions,
  onStageChange,
  targetKind,
  targetKindOptions,
  onTargetKindChange,
  targetNote,
  targetFilters,
  onTargetFiltersChange,
  targetFiltersAvailableProperties,
  targetSelection,
  onTargetSelectionChange,
  targetMaxDepth,
  onTargetMaxDepthChange,
  targetContextSystemName,
  onTargetContextSystemNameChange,
  targetContextOptions,
  targetActionId,
  onTargetActionIdChange,
  targetActionOptions,
  hook,
  onHookChange,
  tier,
  onTierChange,
  editable,
  onApply,
  onRemove,
  className = '',
}) => {
  const { t } = useTranslation();
  const ns = 'studio.arcEditor.inspector.action';
  // Remove is consequential (the spec's reversal table) — the inline confirm states the effect in
  // place; "Keep" returns focus to the button that opened it.
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const removeButtonRef = React.useRef<Button>(null);
  const keepAction = () => {
    setConfirmingRemove(false);
    focusPrimeButton(removeButtonRef.current);
  };

  // The same canonical-tree pattern `ApplicabilityEditorMolecule`/`RelatedSpheresEditorMolecule`
  // use for a `FilterState`-shaped field: top-level `propertyFilters` fold into the root group's
  // own leaves so the tree is the single source of truth, and every edit writes back through
  // `filterGroups` only (`propertyFilters: []`).
  const targetRootGroup = React.useMemo<FilterGroup>(() => {
    const groups = targetFilters?.filterGroups ?? [];
    const leaves = (targetFilters?.propertyFilters ?? []).map(topLevelRowToLeaf);
    const seededGroups = seedLeavesIntoGroups(groups, leaves);
    const stableGroups = withStableFilterGroupIds(seededGroups);
    if (stableGroups.length === 1) return simpleGroupToUnified(stableGroups[0]);
    return {
      id: nextFilterId(),
      operator: 'and',
      expressions: [],
      groups: stableGroups.map(simpleGroupToUnified),
    };
  }, [targetFilters]);

  const handleTargetFiltersChange = React.useCallback(
    (next: FilterGroup) => {
      onTargetFiltersChange({
        perspectiveFilters: [],
        ...targetFilters,
        propertyFilters: [],
        filterGroups: [unifiedGroupToSimple(next)],
      });
    },
    [targetFilters, onTargetFiltersChange]
  );

  const selectionOptions = [
    { value: 'first' as const, label: t(`${ns}.runsOnConfig.selectionFirstLabel`), disabled: !editable },
    { value: 'all' as const, label: t(`${ns}.runsOnConfig.selectionAllLabel`), disabled: !editable },
  ];

  return (
    <div
      className={`arc-action-inspector ${className}`.trim()}
      data-datum="side.panel#5"
      data-kind="panel"
      data-container="side"
    >
      <div className="arc-action-inspector__body">
        <p className="arc-action-inspector__sentence" data-datum="side.acactionsentence-x-stages" data-kind="callout">
          {sentence}
        </p>

        <section className="arc-action-inspector__section" data-container="side > panel#5 > group:children">
          <span className="arc-action-inspector__section-label" data-datum="side.label#26" data-kind="heading">
            {t(`${ns}.whatItDoesHeading`)}
          </span>
          <TypeFieldRowMolecule
            label={t(`${ns}.actionLabel`)}
            hint={t(`${ns}.actionHint`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            datum="side.action"
            controls={[
              {
                kind: 'select',
                value: actionType,
                options: actionTypeOptions,
                onChange: (value) => onActionTypeChange(value as ArcActionAuthoredType),
                ariaLabel: t(`${ns}.actionLabel`),
                datumKind: 'select',
              },
            ]}
          />
          <p className="arc-action-inspector__note" data-datum="side.type-note" data-kind="note">
            {typeNote}
          </p>

          {refOptions && (
            <div className="arc-action-inspector__refpick">
              <TypeFieldRowMolecule
                label={refLabel}
                layout="stacked"
                mode={editable ? 'edit' : 'disabled'}
                datum="side.select#2"
                errorMessage={refErrorMessage}
                controls={[
                  {
                    kind: 'select',
                    value: refValue,
                    options: [{ value: '', label: t(`${ns}.refChoosePlaceholder`) }, ...refOptions],
                    onChange: onRefChange,
                    ariaLabel: refLabel,
                    datumKind: 'select',
                  },
                ]}
              />
              {refChip && (
                <TagAtom
                  label={refChip.label}
                  icon={refChip.icon}
                  size="small"
                  data-datum="side.chip#2"
                  data-kind="chip"
                />
              )}
            </div>
          )}
        </section>

        <div className="arc-action-inspector__two">
          <TypeFieldRowMolecule
            label={t(`${ns}.stageLabel`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            datum="side.stage#2"
            controls={[
              {
                kind: 'select',
                value: stageId,
                options: stageOptions,
                onChange: onStageChange,
                ariaLabel: t(`${ns}.stageLabel`),
                datumKind: 'select',
              },
            ]}
          />
          <TypeFieldRowMolecule
            label={t(`${ns}.runsOnLabel`)}
            hint={t(`${ns}.runsOnHint`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            datum="side.runs-on"
            controls={[
              {
                kind: 'select',
                value: targetKind,
                options: targetKindOptions,
                onChange: onTargetKindChange,
                ariaLabel: t(`${ns}.runsOnLabel`),
                datumKind: 'select',
              },
            ]}
          />
        </div>
        <p className="arc-action-inspector__note" data-datum="side.actarget-x-target-note" data-kind="note">
          {targetNote}
        </p>

        {targetKind === 'descendant' && (
          <section
            className="arc-action-inspector__section arc-action-inspector__runs-on-config"
            data-testid="arc-action-inspector-target-descendant"
            data-container="side > panel#5 > group:children"
          >
            <span className="arc-action-inspector__section-label" data-kind="heading">
              {t(`${ns}.runsOnConfig.descendant.filtersHeading`)}
            </span>
            <UnifiedFilterTreeMolecule
              value={targetRootGroup}
              onChange={handleTargetFiltersChange}
              availableProperties={targetFiltersAvailableProperties}
              allowGroups={false}
              readOnly={!editable}
              container="side"
              datumScope="action-target-descendant-filter-tree"
            />
            <div className="arc-action-inspector__two">
              <TypeFieldRowMolecule
                label={t(`${ns}.runsOnConfig.selectionLabel`)}
                layout="stacked"
                mode={editable ? 'edit' : 'disabled'}
                controls={[
                  {
                    kind: 'custom',
                    ariaLabel: t(`${ns}.runsOnConfig.selectionLabel`),
                    node: (
                      <SegmentedControlAtom
                        mode="single"
                        value={targetSelection}
                        onChange={(value) => onTargetSelectionChange(value as 'first' | 'all')}
                        options={selectionOptions}
                        ariaLabel={t(`${ns}.runsOnConfig.selectionLabel`)}
                      />
                    ),
                  },
                ]}
              />
              <ArcActionTargetMaxDepthField
                value={targetMaxDepth}
                onChange={onTargetMaxDepthChange}
                editable={editable}
                label={t(`${ns}.runsOnConfig.descendant.maxDepthLabel`)}
                hint={t(`${ns}.runsOnConfig.descendant.maxDepthHint`)}
                errorText={t(`${ns}.runsOnConfig.descendant.maxDepthError`)}
              />
            </div>
          </section>
        )}

        {targetKind === 'context_member' && (
          <section
            className="arc-action-inspector__section arc-action-inspector__runs-on-config"
            data-testid="arc-action-inspector-target-context-member"
            data-container="side > panel#5 > group:children"
          >
            <div className="arc-action-inspector__two">
              <TypeFieldRowMolecule
                label={t(`${ns}.runsOnConfig.contextMember.contextLabel`)}
                layout="stacked"
                mode={editable ? 'edit' : 'disabled'}
                controls={[
                  {
                    kind: 'select',
                    value: targetContextSystemName,
                    options: [
                      { value: '', label: t(`${ns}.runsOnConfig.contextMember.contextPlaceholder`) },
                      ...targetContextOptions,
                    ],
                    onChange: onTargetContextSystemNameChange,
                    ariaLabel: t(`${ns}.runsOnConfig.contextMember.contextLabel`),
                    datumKind: 'select',
                  },
                ]}
              />
              <TypeFieldRowMolecule
                label={t(`${ns}.runsOnConfig.selectionLabel`)}
                layout="stacked"
                mode={editable ? 'edit' : 'disabled'}
                controls={[
                  {
                    kind: 'custom',
                    ariaLabel: t(`${ns}.runsOnConfig.selectionLabel`),
                    node: (
                      <SegmentedControlAtom
                        mode="single"
                        value={targetSelection}
                        onChange={(value) => onTargetSelectionChange(value as 'first' | 'all')}
                        options={selectionOptions}
                        ariaLabel={t(`${ns}.runsOnConfig.selectionLabel`)}
                      />
                    ),
                  },
                ]}
              />
            </div>
          </section>
        )}

        {targetKind === 'created_by_action' && (
          <section
            className="arc-action-inspector__section arc-action-inspector__runs-on-config"
            data-testid="arc-action-inspector-target-created-by-action"
            data-container="side > panel#5 > group:children"
          >
            <TypeFieldRowMolecule
              label={t(`${ns}.runsOnConfig.createdByAction.actionLabel`)}
              layout="stacked"
              mode={editable ? 'edit' : 'disabled'}
              controls={[
                {
                  kind: 'select',
                  value: targetActionId,
                  options: [
                    { value: '', label: t(`${ns}.runsOnConfig.createdByAction.actionPlaceholder`) },
                    ...targetActionOptions,
                  ],
                  onChange: onTargetActionIdChange,
                  ariaLabel: t(`${ns}.runsOnConfig.createdByAction.actionLabel`),
                  datumKind: 'select',
                },
              ]}
            />
            {targetActionOptions.length === 0 && (
              <p className="arc-action-inspector__note" data-kind="note">
                {t(`${ns}.runsOnConfig.createdByAction.actionEmptyNote`)}
              </p>
            )}
          </section>
        )}

        <section className="arc-action-inspector__section" data-container="side > panel#5 > group:children">
          <span className="arc-action-inspector__section-label" data-kind="heading">
            {t(`${ns}.whenItRunsHeading`)}
          </span>
          <ChoiceCardGroupAtom
            ariaLabel={t(`${ns}.whenItRunsHeading`)}
            value={hook}
            onChange={(value) => onHookChange(value as ArcActionHook)}
            disabled={!editable}
            options={HOOK_OPTIONS.map((option) => ({
              value: option.value,
              label: t(`${ns}.hook.${option.value}Label`),
              note: t(`${ns}.hook.${option.value}Note`),
              icon: option.icon,
              datum: 'side.button#8',
              datumKind: 'button',
            }))}
          />
        </section>

        <section className="arc-action-inspector__section" data-container="side > panel#5 > group:children">
          <span className="arc-action-inspector__section-label" data-kind="heading">
            {t(`${ns}.howMuchTrustedHeading`)}
          </span>
          <ChoiceCardGroupAtom
            ariaLabel={t(`${ns}.howMuchTrustedHeading`)}
            value={tier}
            onChange={(value) => onTierChange(value as AutomationTier)}
            disabled={!editable}
            variant="ladder"
            options={TIER_OPTIONS.map((option) => ({
              value: option.value,
              label: t(`${ns}.tier.${option.value}Label`),
              held: option.held,
              datum: 'side.choice-card',
            }))}
          />
          <p className="arc-action-inspector__note" data-datum="side.note#3" data-kind="note">
            {t(TIER_OPTIONS.find((o) => o.value === tier)?.held ? `${ns}.tierHeldNote` : `${ns}.tierRunsNote`)}
          </p>
        </section>
      </div>
      {editable && confirmingRemove && (
        <InlineConfirmMolecule
          tone="danger"
          testId="arc-action-inspector-remove-confirm"
          titleId="arc-action-inspector-remove-confirm-title"
          title={t(`${ns}.removeConfirm.title`)}
          body={t(`${ns}.removeConfirm.body`)}
          actions={[
            {
              label: t(`${ns}.removeConfirm.accept`),
              onClick: onRemove,
              variant: 'danger',
              'data-testid': 'arc-action-inspector-remove-confirm-accept',
            },
            {
              label: t(`${ns}.removeConfirm.keep`),
              onClick: keepAction,
              variant: 'text',
              'data-testid': 'arc-action-inspector-remove-confirm-dismiss',
            },
          ]}
        />
      )}
      {editable && (
        <div className="arc-action-inspector__actions" data-container="side > actions">
          <ButtonAtom variant="secondary" size="small" onClick={onApply} data-datum="side.apply#4" data-kind="button">
            {t(`${ns}.apply`)}
          </ButtonAtom>
          <ButtonAtom
            variant="danger"
            size="small"
            icon={<GlyphAtom glyph="lucide:trash-2" aria-hidden />}
            ref={removeButtonRef}
            onClick={() => setConfirmingRemove(true)}
            data-datum="side.remove#2"
            data-kind="button"
          >
            {t(`${ns}.removeAction`)}
          </ButtonAtom>
        </div>
      )}
    </div>
  );
};

ArcActionInspectorMolecule.displayName = 'ArcActionInspectorMolecule';

export default ArcActionInspectorMolecule;
